import { core } from '@musinsa/pbo-core'
import { ReturnManagementFlagChoice, ReturnManagementRow } from '@/services/return/returnManagement/types'

/** unknown 값을 화면 표기용 문자열로 좁힌다. 객체는 대상이 아니므로 빈 문자열로 둔다. */
const asText = (value: unknown): string => (typeof value === 'string' || typeof value === 'number' ? String(value) : '')

/** 응답의 `yyyyMMddHHmmss` 문자열을 화면 표기로 끊는다. */
const toDateTimeText = (value: unknown) => {
  const s = asText(value)
  if (s.length !== 14) return s
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)} ${s.slice(8, 10)}:${s.slice(10, 12)}:${s.slice(12, 14)}`
}

/**
 * 검색(FLAG) 셀 옵션 — 셸 `V2Page.generated.tsx` 와 같은 순서(N·Y)다. 값은 화면 표기 그대로 두고
 * 저장 계약의 boolean 으로는 `toWriteBody` 에서 한 번만 바꾼다(types.ts `toFlagBoolean`).
 *
 * 값 타입을 `ReturnManagementFlagChoice` 로 묶어 행 계약과 갈라지지 않게 한다(리뷰 반영).
 * 빈 칸(`''`)은 응답이 null 일 때의 표시라 선택지에서 뺀 타입을 쓴다.
 */
export const FLAG_OPTIONS: { value: ReturnManagementFlagChoice; label: string }[] = [
  { value: 'N', label: 'N' },
  { value: 'Y', label: 'Y' },
]

interface ColumnCallbacks {
  onHelperOpen: (record: ReturnManagementRow) => void
  onMediaOpen: (kind: 'video' | 'attach', record: ReturnManagementRow) => void
  /** 반품처(RL_CHANNEL) 셀 옵션 — GET /api/v1/combos 응답(화면에서 주입, LOGISPRD-12385). */
  rlChannelOptions?: { value: string; label: string }[]
}

/**
 * `helper`·`video`·`attach` variant 의 콜백(`onHelperOpen`·`onMediaOpen`) 은 pbo-core 0.7.8 런타임에는
 * 있지만 게시된 `.d.ts`(`components-desktop/table/index.d.ts`) 에는 빠져 있다(핸드오프 README 의
 * "게시 지연" 실측과 같은 종류 — dist 번들 실측으로 존재를 확인했다, 계획서 3.7).
 * 컬럼 배열 전체를 이 타입으로 두고 반환 시 공개 타입으로 좁힌다.
 */
type ColumnWithRuntimeVariantProps = Omit<core.CoreColumnType<ReturnManagementRow>, 'variantProps'> & {
  variantProps?: Record<string, unknown>
}

/**
 * RGR04 표 컬럼 37개 — 셸 `V2Page.generated.tsx` COLUMNS 36개를 그대로 옮기되(헤더 문구·폭·순서·
 * `fixed`·`align`·`required`), `dataIndex` 를 응답 카멜케이스로 바꾸고 `variant` 를 채운다(계획서 4.3).
 * 셸에 없는 SKUID(LOGISPRD-12866)를 바코드 뒤에 하나 더했다.
 *
 * `FLAG`(검색)는 셸과 같은 `select` 편집 셀이다 — 저장 계약에 `flag` 가 생겨(moms-be #3666,
 * LOGISPRD-12604) 편집 값을 서버로 보낼 수 있다. `RTN_EXC_DIV`(반품/교환)는 응답에 없는 컬럼이라
 * 컬럼은 남기고 빈 칸으로 둔다(D8).
 */
export const buildColumns = ({
  onHelperOpen,
  onMediaOpen,
  rlChannelOptions = [],
}: ColumnCallbacks): core.CoreColumnType<ReturnManagementRow>[] => {
  const columns: ColumnWithRuntimeVariantProps[] = [
  { key: '__no', title: 'No', width: 48, align: 'center', fixed: 'left', render: (_v, _r, i: number) => i + 1 },
  { key: 'ordNo', title: '반품번호', dataIndex: 'ordNo', width: 167, fixed: 'left', variant: 'input' },
  {
    key: 'flag',
    title: '검색',
    dataIndex: 'flag',
    width: 100,
    align: 'center',
    variant: 'select',
    variantProps: { options: FLAG_OPTIONS },
  },
  { key: 'sprNm', title: '화주코드', dataIndex: 'sprNm', width: 140, required: true },
  { key: 'plnDate', title: '반품일자', dataIndex: 'plnDate', width: 162, required: true, variant: 'datepicker' },
  { key: 'trsInvoiceNo', title: '반송번호', dataIndex: 'trsInvoiceNo', width: 176, required: true, variant: 'input' },
  { key: 'ordOptNo', title: '입고번호', dataIndex: 'ordOptNo', width: 176, variant: 'input' },
  {
    key: 'rlChannel',
    title: '반품처',
    dataIndex: 'rlChannel',
    width: 130,
    variant: 'select',
    variantProps: { options: rlChannelOptions },
  },
  // refNo2·refNo3 는 셸에 `required:true` 로 있지만 저장 계약(계획서 3.2)은 둘 다 optional 이다 —
  // MUSINSA·29CM 화주가 refNo1·refNo2 를 바꿀 때만 서버가 원출고 존재를 조건부로 검증할 뿐, 클라이언트
  // 필수 검사(REQUIRED_FIELDS)에도 없다. 셸 마크와 실제 계약이 어긋나 별표를 떼어 계약을 따른다.
  { key: 'refNo2', title: '원일련번호', dataIndex: 'refNo2', width: 194, variant: 'input' },
  {
    key: 'refNo3',
    title: '송장',
    dataIndex: 'refNo3',
    width: 176,
    variant: 'helper',
    variantProps: { onHelperOpen: (record: unknown) => onHelperOpen(record as ReturnManagementRow) },
  },
  { key: 'barcode', title: 'SKU', dataIndex: 'barcode', width: 167, variant: 'input' },
  { key: 'skuId', title: 'SKUID', dataIndex: 'skuId', width: 150 },
  { key: 'procNm', title: '비고', dataIndex: 'procNm', width: 200, variant: 'input' },
  { key: 'refNo1', title: '거래처주문번호', dataIndex: 'refNo1', width: 176, variant: 'input' },
  { key: 'gdsCd', title: '상품코드', dataIndex: 'gdsCd', width: 140, variant: 'input' },
  { key: 'gdsNm', title: '상품명', dataIndex: 'gdsNm', width: 269, variant: 'input' },
  { key: 'gdsOpt', title: '사이즈', dataIndex: 'gdsOpt', width: 90, variant: 'input' },
  { key: 'ordQty', title: '수량', dataIndex: 'ordQty', width: 100, align: 'right', required: true, variant: 'amountInput' },
  { key: 'trsNm', title: '수하인', dataIndex: 'trsNm', width: 108, required: true, variant: 'input' },
  { key: 'trsPhone', title: '휴대폰', dataIndex: 'trsPhone', width: 167, variant: 'input' },
  { key: 'trsNumber', title: '전화', dataIndex: 'trsNumber', width: 160, variant: 'input' },
  { key: 'trsAddr', title: '주소', dataIndex: 'trsAddr', width: 280, required: true, variant: 'input' },
  { key: 'salChannel', title: '판매업체', dataIndex: 'salChannel', width: 160, variant: 'input' },
  { key: 'brdNm', title: '브랜드명', dataIndex: 'brdNm', width: 160, variant: 'input' },
  { key: 'trsLspNm', title: '반품택배', dataIndex: 'trsLspNm', width: 126, variant: 'input' },
  { key: 'etc1', title: '반품계좌', dataIndex: 'etc1', width: 205, variant: 'input' },
  { key: 'etc2', title: '반송금액', dataIndex: 'etc2', width: 100, align: 'right', variant: 'amountInput' },
  { key: 'etc3', title: '착불금액', dataIndex: 'etc3', width: 100, align: 'right', variant: 'amountInput' },
  { key: 'etc4', title: '선/착불', dataIndex: 'etc4', width: 90, variant: 'input' },
  {
    key: 'videoCount',
    title: '동영상',
    dataIndex: 'videoCount',
    width: 100,
    align: 'center',
    variant: 'video',
    variantProps: { onMediaOpen: (record: unknown) => onMediaOpen('video', record as ReturnManagementRow) },
  },
  { key: 'etc5', title: '정불구분', dataIndex: 'etc5', width: 100, variant: 'input' },
  {
    key: 'atchFileCount',
    title: '첨부',
    dataIndex: 'atchFileCount',
    width: 80,
    align: 'center',
    variant: 'attach',
    variantProps: { onMediaOpen: (record: unknown) => onMediaOpen('attach', record as ReturnManagementRow) },
  },
  { key: 'insDate', title: '생성일시', dataIndex: 'insDate', width: 160, render: toDateTimeText },
  { key: 'insBy', title: '생성자', dataIndex: 'insBy', width: 100 },
  { key: 'updDate', title: '수정일시', dataIndex: 'updDate', width: 160, render: toDateTimeText },
  { key: 'updBy', title: '수정자', dataIndex: 'updBy', width: 100 },
  // TODO(api): RTN_EXC_DIV — 조회 응답(ReturnManagementDto)에 없다. rsnCode·rtnType 파생 규칙 또는
  // DTO 필드 추가가 필요하다(계획서 7.3 BE 요청 대상). 컬럼은 남기고 빈 칸으로 둔다(D8, LLD 7.1 결정 10 —
  // "빈 칸"이지 '-' 가 아니다).
  { key: 'rtnExcDiv', title: '반품/교환', width: 100, render: () => '' },
  ]

  return columns as core.CoreColumnType<ReturnManagementRow>[]
}

const STATIC_COLUMNS = buildColumns({ onHelperOpen: () => undefined, onMediaOpen: () => undefined })

/** 엑셀 다운로드·업로드가 쓰는 (헤더 제목 → 행 키) 대응. 표 컬럼 순서를 그대로 따른다. */
export const EXCEL_COLUMNS = STATIC_COLUMNS.filter((c) => c.dataIndex).map((c) => ({
  title: asText(c.title),
  key: asText(c.dataIndex),
}))

/**
 * 엑셀 업로드로 값을 채울 수 있는 컬럼 — 편집 셀(input·amountInput·select·datepicker·helper)만.
 * `video`·`attach`(개수 표시)와 비데이터 컬럼(No·반품/교환)은 뺀다. 검색(FLAG)은 편집 셀이라
 * 포함되고, 'Y'·'N' 밖의 값은 파서가 파일을 물린다(excelImport.ts `toFlagCellText`).
 */
const IMPORTABLE_VARIANTS = new Set(['input', 'amountInput', 'select', 'datepicker', 'helper'])
export const IMPORTABLE_KEYS = STATIC_COLUMNS.filter((c) => c.dataIndex && c.variant && IMPORTABLE_VARIANTS.has(c.variant)).map((c) =>
  asText(c.dataIndex),
)
